import React from 'react';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';

// Components
import Container from 'components/common/Layout/Container';
import Main from 'components/common/Layout/Main';
import Doctor from 'components/templates/dr';
import Head from 'next/head';
import BreadCrumbs from 'components/common/BreadCrumbs';

// Libs
import { appConfig, apiConfig } from 'configs';
import Translation from 'libs/Translation';
import Util from 'libs/Util';

const DR = ({ doctor = {} }) => {
	const router = useRouter();
	const {webConfig} = useSelector(state => state.core);
	const { query } = router;
	let specialties = '';
	doctor?.specialtyDetails?.forEach((item, index) => {
		const s = Translation.t('label.specialty-education', {degree: item?.EducationLevel, specialty: item?.Specialty});
		specialties = `${specialties} ${s}${index !== doctor?.specialtyDetails?.length - 1 ? Translation.t(',') : ''}`;
	})

	const breadCrumbs = [
        { key: 0, label: Translation.t('title.default'), to: '/', params: webConfig.lang !== 'fa' ? {lang: webConfig.lang} : {}},
        { key: 1, label: Translation.t('title.doctors'), to: '/explore/doctors', params: webConfig.lang !== 'fa' ? {lang: webConfig.lang} : {} },
        { key: 2, label: Translation.t('title.doctors-specialty', {specialty: doctor.specialtyDetails?.length && doctor.specialtyDetails[0]?.Specialty}), to: doctor?.specialtyDetails?.length && `/explore/doctors/all/${doctor?.specialtyDetails[0]?.SpecialtySlug}/city/IR`, params: webConfig.lang !== 'fa' ? {lang: webConfig.lang} : {} },
        { key: 3, label: Translation.t('title.doctors-specialty-city', {specialty: doctor.specialtyDetails?.length && doctor.specialtyDetails[0]?.Specialty, city: doctor?.profiles?.HealthProviderProfiles?.length ? webConfig.lang !== 'fa' ? doctor?.profiles?.HealthProviderProfiles[0]?.cityDetails?.Slug : doctor?.profiles?.HealthProviderProfiles[0]?.cityDetails?.Title : null}), to: doctor?.specialtyDetails?.length && `/explore/doctors/all/${doctor?.specialtyDetails[0]?.SpecialtySlug}/city/${doctor?.profiles?.HealthProviderProfiles[0]?.cityDetails?.Slug}`, params: webConfig.lang !== 'fa' ? {lang: webConfig.lang} : {} },
        { key: 4, label: `${doctor?.profiles?.Label} ${doctor?.profiles?.FirstName} ${doctor?.profiles?.LastName}`, to: `/dr/${doctor?.profiles?.Slug}`, params: webConfig.lang !== 'fa' ? {lang: webConfig.lang} : {} },
    ]

	const { seo: { title, description }, addTitlePostfix } = Util.prepareSeo(
		'doctor',
		{
			name: `${doctor?.profiles?.Label} ${doctor?.profiles?.FirstName} ${doctor?.profiles?.LastName}`,
			description: `${Translation.t('text.online-appointment')} ${doctor?.profiles?.Label} ${doctor?.profiles?.FirstName} ${doctor?.profiles?.LastName} ${specialties}`,
		},
	);

	const breadCrumb = {
		itemListElements: [
			{
				position: 1,
				name: Translation.t('title.default'),
				item: 'https://vclinic.io',
			},
			{
				position: 2,
				name: `${doctor?.profiles?.Label} ${doctor?.profiles?.FirstName} ${doctor?.profiles?.LastName}`,
				item: `https://vclinic.io/dr/${query.name}`,
			},
		],
	};

	const seo = {
		title: `${title} | ${specialties} - ${Translation.t('title.default')}`,
		h1Title: title,
		description,
		additionalMetaTags: [
			{ name: 'twitter:card', content: 'summary_large_image' },
			{ name: 'twitter:site', content: '@vclinic_io' },
			{ name: 'twitter:title', content:  `${title} | ${specialties} - ${Translation.t('title.default')}` },
			{ name: 'twitter:description', content: description },
			{ name: 'twitter:creator', content: '@vclinic_io' },
			{ name: 'twitter:image', content: doctor?.profiles?.profileImage ? doctor?.profiles?.profileImage?.fileUrl : 'https://vclinic.io/images/doctors/default.png' },
		],
		openGraph: {
			title: `${title} | ${specialties} - ${Translation.t('title.default')}`,
			description,
			type: 'profile',
			site_name: Translation.t('label.virtual-clinic'),
			images: [
				{
					url: doctor?.profiles?.profileImage ? doctor?.profiles?.profileImage?.fileUrl : 'https://vclinic.io/images/doctors/default.png',
					type: 'image/jpg',
					alt: `${doctor?.profiles?.Label} ${doctor?.profiles?.FirstName} ${doctor?.profiles?.LastName} ${specialties}`,
					width: '250',
					height: '250',
				},
			],
			doctor: {
				services: doctor?.profiles?.Services?.length > 0 ? doctor.profiles.Services.map(s => {
					const j = s;
					return j;
				}) : [],
				specialties: doctor?.specialtyDetails?.length > 0 ? doctor.specialtyDetails.map(s => {
					const j = s.Specialty;
					return j;
				}) : [],
				clinics: doctor?.profiles?.HealthProviderProfiles?.length > 0 ? doctor.profiles.HealthProviderProfiles.map(h => {
					const j = h.Name;
					return j;
				}) : [],
			},
		},
	};
	
    return (
		<Main breadCrumb={breadCrumb} seo={seo} addTitlePostfix={addTitlePostfix}>
			<Head>
				<script
					type="application/ld+json"
					dangerouslySetInnerHTML={{
						__html: JSON.stringify(Util.makeDrProfileJsonLd(doctor)),
					}}
				/>
			</Head>
			<Container>
				<div 
					style={{marginBottom: '160px'}}
				>
            		<Doctor 
						doctor={doctor}
						webConfig={webConfig}
					/>
					<BreadCrumbs 
            			list={breadCrumbs} 
        			/>
				</div>
        	</Container>
		</Main>
        
    )
}

export default DR;

export const getServerSideProps = async ({ query }) => {
	if (query.name) {
		let result;
		let data;
		let notFound = false;

		let lang = 'fa';
		if (query.lang) {
			lang = query.lang;
		}

		try {
			result = await Promise.all([
				fetch(`${appConfig.nest_api_base_url}${apiConfig.HWProfile.endpoint}/${query.name}?language=${lang}`)
			]);
			
			if (result) {
				data = await Promise.all([
					result[0].json(),
				])
			}
			if (data[0]?.status === false) {
				notFound = true;
			}
		}
		catch (error) {
			if (error) {
				return {
					notFound: true,
				};
			}
		}
		return {
			props: {
				doctor: data[0]?.data?.data?.list[0] ?? {},
			},
			notFound: notFound,
		};
	}
};